// =============================================================================
// ui/tabs/Journal.tsx — the PHASED journal.
// -----------------------------------------------------------------------------
// ⚠ THE PHASED LAYOUT, NOT THE CLASSIC ONE. The old client ships both and picks
// at runtime on `appFlags.journalLayout`. `migrateState` sets that to `'phased'`
// for everyone (gated by `uiPhasedDefaults`), and the live beta prefs row
// confirms it — Josh's value is `"phased"`.
//
// The classic template reads FIRST in the old file and the Settings dropdown even
// treats an unset flag as `'classic'`, so reading the source top-down leads
// straight to the wrong branch. That is exactly how the Settings port went wrong
// once already; this time the flag was checked against the database before a line
// was written.
//
// ── 🚨 DO NOT LIST WHAT IS HERE. THIS COMMENT ALREADY ROTTED ONCE ──
//
// It used to open "Read-only except the EOD recap" and then enumerate what was
// missing: "no autosave, no readiness gate, no session-contract editor" — and the
// comment went on describing a tab that had not existed for weeks. It was found
// by Josh asking a plain question ("is the Journal done?"), not by anything in
// the suite.
//
// 🔴 THEN IT ROTTED A SECOND TIME, IN THE OPPOSITE DIRECTION, AND THAT ONE COST
// JOSH A MORNING. The correction above said "Every one of those shipped" and
// named four components. **Three of the four shipped. Autosave did not.**
// `PlanEditor` existed and wrote only when its Save button was pressed, so a plan
// typed and left by switching to the Session phase was gone — the phase switch
// unmounts the panel and the draft lived in `useState`. The comment asserting
// autosave was present is the reason nobody went looking for it (TCR-79).
//
// ⚠ So the failure mode is not "a list goes out of date". It is that a list can
// be wrong in EITHER direction, and claiming a safeguard that does not exist is
// far worse than naming a gap that has been filled. Autosave now exists —
// `ui/state/useAutosave.ts`, on the plan and the recap — but do not take this
// sentence as evidence of that either. Read the module.
//
// That is D68 exactly, and it is the third hand-kept inventory in this client to
// go stale (the journal's own on-screen card, the FilterDrawer's, this one). So
// the rule, not the list:
//
//   **anything on this tab with its own Save or Lock button writes from this
//   client, and a new editor arrives WITH its button.**
//
// What is genuinely absent has no control on the tab at all, and the on-screen
// card near the top says which — that copy is the one to trust, because
// `Journal.test.tsx` asserts it never names a feature whose editor is rendered.
// A comment cannot be tested, so this one deliberately states no inventory.
//
// ── WHAT THE RECAP STATS SCOPE TO ──
//
// The day's trades, matched on `effectiveTradeDate` against the JOURNAL date —
// not "today" — and narrowed to the session contract's accounts when it pledged
// any, else all accounts. Ported literally from `_recapStats`.
// =============================================================================

import { useMemo, type ReactNode } from 'react';
import { useAccounts } from '../state/accounts';
import { useContracts } from '../state/contracts';
import { usePnlUnit } from '../state/usePnlUnit';
import { tradeValue } from '../../selectors/pnlUnit';
import { useFilters } from '../state/filters';
import { usePreferences } from '../state/preferences';
import { useJournalTab, type JournalPhase } from '../state/useJournal';
import { useTradeRows } from '../state/useTradeRows';
import { selectKpis, type Kpis } from '../../selectors/kpis';
import { effectiveTradeDate } from '../../selectors/population';
import { tradesCountingForMetrics, type Account } from '../../selectors/accounts';
import { blankEntry, hasCheckInContract, tiltCheck } from '../../selectors/journal';
import {
  isReadinessComplete, readinessGroupsOrDefault, readinessProgress,
  type ReadinessGroup,
} from '../../selectors/readiness';
import type {
  JournalEntry, MidSessionNote, PreMarketPlan, SessionContract, TiltResult,
} from '../../selectors/journal';
import { ForYouTodayCard } from './ForYouTodayCard';
import { JournalSection } from './JournalSection';
import { JournalVisibility } from './JournalVisibility';
import { ReadinessGate } from './ReadinessGate';
import { SessionContractEditor } from './SessionContractEditor';
import { ContractCheckIn } from './ContractCheckIn';
import { MidSessionNotes } from './MidSessionNotes';
import { PlanEditor } from './PlanEditor';
import { RecapEditor } from './RecapEditor';
import { VoiceNotes } from '../media/VoiceNotes';
import { useSupabase } from '../state/supabase';
import { saveRecordings } from '../../io/sync/saveRecordings';
import type { TradeTableRow } from '../../io/sync/trades';
import { dayLabel, money, moneyTarget, pct, unitValue } from '../format';

const PHASES: readonly { id: JournalPhase; icon: string; label: string; sub: string }[] = [
  { id: 'pre', icon: '☀', label: 'Pre-Market', sub: 'Plan the day' },
  { id: 'session', icon: '⚡', label: 'Session', sub: 'Trade & note' },
  { id: 'recap', icon: '🌙', label: 'Recap', sub: 'Review & carry' },
];

/**
 * The chip on each phase card.
 *
 * ⚠ ONLY PRE-MARKET CAN BE "DONE" — it is the one phase with a completion test
 * (the readiness gate). A session is live or it is not, and a recap has no
 * finish line the app can check, so beta leaves both unlabelled rather than
 * inventing a tick nobody earned.
 */
function phaseState(
  id: JournalPhase, readinessDone: boolean,
): { cls: string; text: string } {
  if (id === 'pre') {
    return readinessDone
      ? { cls: ' done', text: 'Ready' }
      : { cls: '', text: 'Incomplete' };
  }
  if (id === 'session') return { cls: ' now', text: 'Live' };
  return { cls: '', text: '' };
}

export function JournalTab() {
  const j = useJournalTab();
  const { accounts } = useAccounts();
  const { contracts } = useContracts();
  // ⚠ TILT CHECK follows the unit. The GLANCE BAND and the ACCOUNT CARD do not
  // — both state figures read against the firm’s dollar rules (#44, #67). Beta
  // does exactly this, two units on one screen; the note on each says why.
  const { unit } = usePnlUnit();
  const { accounts: selection } = useFilters();
  // The global display unit — $ / points / ticks / R. See `UnitPicker`.
  const { prefs } = usePreferences();
  const mode = prefs?.dayBoundaryMode ?? null;

  // The journal date's own trades — its own read, because the journal date moves
  // independently of the global range filter.
  const bounds = useMemo(
    () => (j.date ? { startDate: j.date, endDate: j.date } : null),
    [j.date],
  );
  const dayRows = useTradeRows(bounds, mode);

  const stored = j.byDate?.[j.date] ?? null;

  /**
   * ── 🚨 AN UN-JOURNALLED DAY IS A WORKING SCREEN, NOT A BUTTON ──
   *
   * This read `started === j.date ? blankEntry(j.date) : null`, where `started`
   * was set by a "Start today's plan" button. So every morning — the commonest
   * state this tab has — the whole page was one card and a click: no tile row,
   * no active-rule banner, no carried focus, no form, on all three phases. Beta
   * calls `getOrCreateJournal` and simply draws the day.
   *
   * ⚠ THE BUTTON WROTE NOTHING. It set local React state, and the form it
   * revealed already rendered from `blankEntry` — which is in-memory only. So
   * this has EXACTLY the database effect the click had: none.
   *
   * ── 🔴 AND THE DECISION IT LOOKED LIKE IT CAME FROM SAYS SOMETHING ELSE ──
   *
   * `REBUILD_DECISIONS.md` rules out *materialising a ROW when the user opens a
   * new day*: beta's whole-state sync deletes any date absent from live state,
   * so a row for every date merely opened would manufacture rows for it to
   * churn. That is sound and it STANDS — the upsert on Save is still the only
   * thing that creates a row, and a test asserts opening a day writes nothing.
   *
   * It rules out the ROW. It never ruled out the FORM, and the two were
   * conflated. ⚠ A parity delta is owed to `tradecoach-saas` recording that.
   *
   * ── 🚨 `isNewDay`, NEVER `!stored` — AND IT IS ONE OF TWO GUARDS ──
   *
   * `isNewDay` is `!!byDate && !byDate[date]` — true only once the read has
   * LANDED and this date is genuinely absent. `!stored` cannot tell "no entry"
   * from "no entry YET", and an empty form over a pending read reads as a
   * journal that lost its content, then invites a Save that writes the blank
   * over what could not be fetched. D61: a blank the user confirms is a fully
   * authorised delete.
   *
   * ⚠ THE EARLY RETURN BELOW GUARDS THE SAME HAZARD and the two are REDUNDANT.
   * 🔴 MEASURED, and the first two versions of this comment were both wrong:
   * removing either guard ALONE breaks no test; removing BOTH renders an
   * editable empty journal over a read that has not returned, and the test
   * catches it.
   *
   * ▶ So a green suite here proves only that ONE of them works. Sabotage them
   * together or not at all.
   */
  const entry = stored ?? (j.isNewDay ? blankEntry(j.date) : null);

  // The journal date's trades, matched on the EFFECTIVE date. Shared by the
  // glance band, Tilt Check and the Recap panel so all three agree.
  const dayTrades = useMemo(
    () => (dayRows.rows && mode
      ? dayRows.rows.filter((t) => effectiveTradeDate(t, mode) === j.date)
      : null),
    [dayRows.rows, mode, j.date],
  );

  /**
   * ── 🚨 SCOPED TO THE ACCOUNTS THE CONTRACT PLEDGED, NOT THE GLOBAL FILTER ──
   *
   * This read the toolbar's account filter until now, and the file header above
   * has claimed since the first commit that it was "narrowed to the session
   * contract's accounts … ported literally from `_recapStats`". It was not. The
   * comment described the right rule and the code did something else.
   *
   * It matters most on the surface that came with this change. The check-in
   * judges a trader against a pledge made that morning — "I will stop at
   * −$500 across these three accounts" — and answering it with whatever
   * accounts happen to be ticked in the toolbar answers a different question.
   * With one account filtered in, a breach across the three reads as "on
   * contract".
   *
   * ⚠ EMPTY MEANS ALL, exactly as `AccountSelection` already defines it and
   * exactly as beta's `ids.length ? … : true` does — a contract that pledged no
   * accounts is judged on the whole book.
   *
   * ⚠ AND IT DELIBERATELY IGNORES THE TOOLBAR. Beta's `_recapStats` does not
   * consult the active-account filter at all. Tilt Check does (see below), and
   * beta spells out why in a comment: tilt should reflect everything traded that
   * day, contract or not. Two populations on one screen, on purpose — D77.
   */
  // ⚠ Keyed on the JOINED ids, not the array. `accountIds` is rebuilt by every
  // read, so a raw array dependency re-runs every aggregate on this tab on each
  // render even when the pledge has not moved.
  const contractKey = (entry?.sessionContract.accountIds ?? []).join(',');
  const contractSelection = useMemo(
    () => (contractKey ? contractKey.split(',') : []),
    [contractKey],
  );

/**
   * ── 🚨 THE WHOLE GLANCE BAND IS IN DOLLARS, IN EVERY PHASE (#67) ──
   *
   * It used to follow the display unit, and on Points that broke two things:
   *
   *   • The Session tile printed a POINTS figure through `money()` — Josh's beta
   *     screenshot reads `+$137.75` there, so the value was wrong, not the label.
   *   • Worse, the same number is what the Contract Check-In reaches its verdict
   *     on, and a session contract's target and DLL are DOLLARS. A −$1,000 day
   *     is −200 points on ES, and −200 against a −$500 limit is inside it. **A
   *     real breach of the trader's own stop rendered as "On contract".**
   *
   * Beta has one `_recapStats` for all four phases; it sums `t.core.netPnl` and
   * every tile prints `fmtMoney`. Nothing in it ever sees the unit.
   *
   * ⚠ SO THE RECAP TILES CHANGE TOO — Net P&L, Best and Worst. They converted,
   * beta does not, and leaving them would put two units in one four-tile band.
   * That half is verified from beta's source rather than from a screenshot.
   *
   * ⚠ `unit` is deliberately NOT read here any more. It used to be, and it was
   * missing from the dependency list — so the band did not even re-run when the
   * switch moved. Both problems go away with the unit.
   */
  const glanceUsd = useMemo(() => {
    if (!dayTrades || !accounts || !contracts) return null;
    return selectKpis({
      trades: dayTrades,
      accounts: accounts as readonly Account[],
      contracts,
      selection: contractSelection,
      unit: 'usd',
    });
  }, [dayTrades, accounts, contracts, contractSelection]);

  /**
   * Tilt Check's population — the TOOLBAR's accounts, and metrics-counting only.
   *
   * ⚠ TWO THINGS THE PORT WAS MISSING, both of which inflate it. The trades went
   * in unfiltered, so a SIM or replay account's losses counted toward a losing
   * streak on a real book; and the toolbar filter was ignored, so tilt was
   * measured across accounts the trader was not looking at.
   */
  const tiltTrades = useMemo(() => {
    if (!dayTrades || !accounts) return null;
    return tradesCountingForMetrics(dayTrades, selection, accounts as readonly Account[]);
  }, [dayTrades, accounts, selection]);

  // ⚠ THE CHECKLIST DEFINITIONS COME FROM PREFERENCES, never from the constant.
  // The gear in the old client edits a global list; Josh's stored copy matches
  // the defaults today, so a hardcoded version would look right and diverge the
  // first time anyone touches that gear. `readinessGroupsOrDefault` reproduces
  // the old client's own fallback (missing OR empty → the seed list).
  const readinessGroups = useMemo(
    () => readinessGroupsOrDefault(prefs?.readinessGroups),
    [prefs],
  );
  const readinessDone = entry
    ? isReadinessComplete(entry.readiness.items, readinessGroups) : false;
  const readinessCount = readinessProgress(
    entry?.readiness.items ?? {}, readinessGroups,
  );

  // ⚠ The prior session's "Tomorrow's Focus", carried forward READ-ONLY. The old
  // client searches every earlier entry for the most recent non-empty one — so a
  // gap of unjournalled days does not lose the thread.
  const priorFocus = useMemo(() => {
    if (!j.byDate) return null;
    const prior = Object.values(j.byDate)
      .filter((e) => e.date < j.date && e.eodRecap.tomorrowFocus.trim())
      .sort((a, b) => b.date.localeCompare(a.date))[0];
    return prior ? { date: prior.date, text: prior.eodRecap.tomorrowFocus.trim() } : null;
  }, [j.byDate, j.date]);

  if (j.phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your journal</h3>
        <p className="muted">Nothing has been changed — this is a read problem. {j.error}</p>
      </div>
    );
  }

  if (j.phase === 'loading' || !j.date || !mode) {
    return <p className="muted">Loading your journal…</p>;
  }

  // ⚠ `data-phase` IS WHAT COLOURS THE WHOLE VIEW. Beta hangs three accent
  // variables off it — pre is blue, session orange, recap purple — and every
  // section icon and glow below reads them. Without the attribute the port
  // rendered all three phases identically blue, and the colour that was telling
  // the trader where they are said nothing.
  return (
    <div className="journal-phased" data-phase={j.activePhase}>
      <div className="jp-topbar">
        <div className="row gap-8">
          <button type="button" className="btn" aria-label="Previous day" onClick={() => j.step(-1)}>‹</button>
          <input
            type="date"
            aria-label="Journal date"
            value={j.date}
            onChange={(e) => j.setDate(e.target.value)}
          />
          <button type="button" className="btn" aria-label="Next day" onClick={() => j.step(1)}>›</button>
          <button type="button" className="btn" onClick={j.goToday}>Today</button>
        </div>
      </div>

      {/* ⚠ The snap is EXPLAINED, not silent. In session mode a weekend pick jumps
          forward to the next session day, and an input that appears to move on its
          own reads as a bug. The old client toasts for the same reason. */}
      {j.snappedFrom ? (
        <p className="muted" role="status">
          {j.snappedFrom} is a weekend — there is no CME session then, so this
          moved to {j.date}.
        </p>
      ) : null}

      {/* ⚠ COMPUTED FROM THE ACTIVE CHECKLIST, not read off `completedAt`.
          The old client's pill is `isReadinessComplete(j)`, which walks the
          groups currently in `user_preferences`. The two disagree in both
          directions and one of them is dangerous: tick all twelve (stamping
          `completedAt`), then add a thirteenth item with the gear, and a
          `completedAt`-based pill keeps telling the trader they are ready while
          their own checklist is not. This read the stamp until now.

          It also counts against the ACTIVE items rather than against the keys
          present in the row — a removed item leaves an orphaned key behind, so
          "3 of 13 ticked" was arithmetic over the wrong denominator. */}
      {entry ? (
        <p className={`pill ${readinessDone ? 'green' : 'muted'}`}>
          {readinessDone
            ? 'Readiness ✓'
            : `Readiness incomplete — ${readinessCount.done} of ${readinessCount.total} ticked`}
        </p>
      ) : null}

      {/* ⚠ THE GEAR SITS OUTSIDE THE PHASE STRIP, not inside a section header,
          and that placement is deliberate (TCR-81). `JournalSection` folds on a
          header click; a gear living in one of those headers would make a click
          aimed at it ambiguous between hiding a section and folding it. Beta
          guards the same collision by having its collapse handler ignore clicks
          that land on a control. Keeping the two apart needs no guard. */}
      <div className="jp-daystrip" role="tablist" aria-label="Journal phase">
        {PHASES.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={j.activePhase === p.id}
            className={`jp-phase${j.activePhase === p.id ? ' on' : ''}`}
            data-p={p.id}
            onClick={() => j.setPhase(p.id)}
          >
            <span className="pic" aria-hidden="true">{p.icon}</span>
            <span>
              <span className="plabel">{p.label}</span>
              <span className="psub">{p.sub}</span>
            </span>
            {/* ⚠ THE STATE CHIP WAS MISSING ENTIRELY. It is the only thing on
                the strip that says whether a phase is DONE, and beta puts it on
                every card — "Ready"/"Incomplete" on pre-market, "Live" on the
                session. Without it the three cards are three labels. */}
            <span className={`pstate${phaseState(p.id, readinessDone).cls}`}>
              <span className="dot" />
              {phaseState(p.id, readinessDone).text}
            </span>
          </button>
        ))}
      </div>

      {/* ⚠ THE FIGURES NEVER NEEDED AN ENTRY, and gating them on one hid them on
          the day they are most useful. `glanceUsd` comes from the TRADE BOOK —
          on 2026-09-22 this client held Net P&L +$58.75, Best +$46.00 and Worst
          −$16.00 over four trades and printed none of them, because no journal
          row existed. Beta showed all four.

          It still waits for `entry`, because the band also reports plan and
          readiness state — but `entry` is a blank on an un-journalled day now
          rather than null, so the trade figures arrive with it. The same lesson
          is recorded three lines below for `ForYouTodayCard`, which was moved
          out of this check for exactly this reason and left the band behind. */}
      {entry ? (
        <GlanceBand
          phase={j.activePhase} entry={entry} stats={glanceUsd}
          readinessDone={readinessDone}
        />
      ) : null}

      {/* ⚠ OUTSIDE the entry check, deliberately. It was inside `PrePanel` at
          first, which hid it on exactly the day it is most useful: a blank
          journal the trader has just opened to plan. The card is computed from
          the trade book alone and needs no journal entry — the old client's
          `getOrCreateJournal` means there is always an entry there, so its
          placement inside the panel carries no such condition. */}
      {/* 🔴 ONE GEAR PER PHASE, AND IT SITS BELOW THE PHASE STRIP ON PURPOSE
          (TCR-82). Josh, after seeing a single shared list: *"I think there
          should be a separate drawer for each tab. I don't think one drawer
          should fit all."* Above the strip it read as a global control, which is
          half of why a mixed list was confusing — so it now sits inside the
          phase it affects and its drawer lists only that phase's sections.

          ⚠ NO GEAR ON SESSION, deliberately. That phase renders Tilt Check,
          Contract Check-In and Mid-Session Notes, and two of the three are not
          obviously optional — a tilt warning somebody can switch off is a
          different product decision from a journal section nobody uses. An empty
          drawer would be worse than none; Josh decides whether they qualify. */}
      {j.activePhase === 'pre' || j.activePhase === 'recap' ? (
        <div className="jp-vis-row">
          <JournalVisibility phase={j.activePhase} />
        </div>
      ) : null}

      {j.activePhase === 'pre' ? <ForYouTodayCard /> : null}

      {/* ── ⚠ LOADING IS THE ONLY REASON THERE IS NO ENTRY NOW ──

          This branch was "Nothing journalled for {date}" plus a button to start
          one, and it was the commonest screen on the tab — see the note above
          `entry` for what that cost and why the decision it leant on does not
          cover it. An un-journalled day renders the form from an in-memory
          blank now, so the only way to reach here is a read that has not
          returned.

          ⚠ IT MUST STAY A STATE OF ITS OWN. An empty form over a pending read
          reads as a journal that lost its content, and invites a Save that
          overwrites it. A FAILED read is a third state, handled above. */}
      {!entry ? (
        <div className="card">
          <p className="muted">Loading…</p>
        </div>
      ) : (
        <>
          {j.activePhase === 'pre'
            ? <PrePanel
                plan={entry.preMarketPlan} entry={entry} priorFocus={priorFocus}
                date={j.date}
                rawPlan={j.rawPlanFor(j.date)}
                rawContract={j.rawContractFor(j.date)}
                readinessGroups={readinessGroups}
                baselineUpdatedAt={j.baselineFor(j.date)}
                onSaved={(pl, dqv, ts, raw) => {
                  j.applySavedPlan(
                    j.date,
                    { ...entry, preMarketPlan: pl, disqualifiers: dqv },
                    raw, ts,
                  );
                }}
                onContractSaved={(contract, ts, raw) => {
                  j.applySavedContract(j.date, { ...entry, sessionContract: contract }, raw, ts);
                }}
                onReadinessSaved={(items, completedAt, ts) => {
                  j.applySavedRecap(
                    j.date, { ...entry, readiness: { items, completedAt } }, ts,
                  );
                }}
              />
            : null}
          {j.activePhase === 'session'
            ? <SessionPanel
                entry={entry} date={j.date}
                tilt={tiltTrades ? tiltCheck(tiltTrades.map((t) => ({
                  timeIn: t.timeIn as string | null, timeOut: t.timeOut as string | null,
                  netPnl: t.netPnl, qty: t.qty,
                  // 🚨 A FIELD CALLED `unitValue` THAT WAS FED DOLLARS, exactly as
                  // the Analytics period curve was.
                  //
                  // ⚠ AND IT IS THE ONE FIGURE ON THIS SCREEN THAT DOES CONVERT.
                  // Beta calls `dvTiltCheck(trades, unit)` while its glance band
                  // sums raw dollars — so tilt is in the display unit and the
                  // band above it is not. That reads like an oversight, and it is
                  // beta’s behaviour, which is the standard here (#67).
                  unitValue: tradeValue(t as never, unit, contracts ?? []),
                }))) : null}
                pending={!tiltTrades}
                livePnlUsd={glanceUsd?.totalPnl ?? null}
                rawContract={j.rawContractFor(j.date)}
                baselineUpdatedAt={j.baselineFor(j.date)}
                onCheckIn={(contract, ts, raw) => {
                  j.applySavedContract(j.date, { ...entry, sessionContract: contract }, raw, ts);
                }}
                onNotesSaved={(notes, ts) => {
                  j.applySavedRecap(j.date, { ...entry, midSessionNotes: notes }, ts);
                }}
              />
            : null}
          {j.activePhase === 'recap' ? (
            <RecapPanel
              entry={entry} date={j.date}
              baselineUpdatedAt={j.baselineFor(j.date)}
              onSaved={(e, ts) => { j.applySavedRecap(j.date, e, ts); }}
              onRecordingsSaved={(recordings, ts) => {
                j.applySavedRecordings(j.date, { ...entry, recordings }, ts);
              }}
            />
          ) : null}
        </>
      )}

      {/* ⚠ THE CUT LINE, and it MOVED. This card used to say the pre-market plan
          was display-only; the plan editor then shipped and the copy did not
          follow it, so the screen contradicted itself with the form sitting a few
          hundred pixels above the sentence denying it existed. Caught by reading
          the live page, not by any test.

          What is still out is out for a reason, not timidity: the session
          contract and readiness gate record STATE DURING THE SESSION (when a stop
          was hit, when it was acknowledged — which flags every later trade as an
          override), so a planning form must not drive them. See D62. */}
      <div className="card">
        <h3>What’s writable here</h3>
        {/* ⚠ PHRASED SO IT CANNOT ROT. Three times today this card listed a
            surface as read-only while that surface's editor rendered a few
            hundred pixels above it — the plan, then the contract, then the
            readiness gate. Each time the fix was to rewrite the inventory, and
            each time the next feature invalidated it again.

            So it no longer enumerates what saves. "Anything with a Save or Lock
            button" stays true when the next editor lands, because a new editor
            arrives WITH its button. What it does list is features that have no
            control on this tab at all — and a test asserts this card never names
            something whose editor is on screen. */}
        {/* ⚠ MID-SESSION NOTES CAME OFF THIS LIST when they became writable, in
            the same commit. That is the discipline the card depends on: the
            sentence is only trustworthy if it is edited by whoever ships the
            control, and it has gone stale three times by being edited later. */}
        <p className="muted">
          {/* 🔴 THE EMPHASISED CLAIM MUST BE THE AFFIRMATIVE ONE, AND IT WAS NOT.
              This led in bold with "There is no single Save Journal button here"
              — printed at the BOTTOM OF EVERY PHASE, so on Recap it landed two
              inches under RecapEditor's own "Save recap" button. Josh read it
              exactly as written and asked: "Do we need to press that button to
              save or not?" Nothing was factually wrong; the bold was on the
              denial and the answer was in the sentence after it.

              ▶ The rule this card leaves behind: when a control labelled Save is
              on screen, the emphasis goes on USING it. A negation can follow, it
              cannot lead. A guard in Journal.test.tsx asserts that. */}
          {/* 🔴 REWRITTEN BY TCR-79, AND THE OLD WORDING WAS A SYMPTOM. It read
              "Each section saves itself — press the Save, Lock or Add button on
              the section you edited", which was true of the behaviour and wrote
              the MISSING AUTOSAVE UP AS THE DESIGN. Josh then lost a premarket
              plan to a phase switch. Copy that explains a gap confidently is how
              a gap stops looking like one. */}
          <b>Your writing saves itself as you go</b> — each field is written when you
          move off it, so switching phase or leaving the tab cannot lose it. The{' '}
          <b>Save</b> button is there if you want to be certain; you do not need it.{' '}
          {/* ⚠ THE ONE EXCEPTION, AND IT IS DELIBERATE — see the header of
              SessionContractEditor.tsx. Lock IS the commit there, so a target
              typed and not locked is working state. Beta behaves the same way:
              typing a number is not pledging it.

              ⚠ DESCRIBED WITHOUT NAMING THE SECTION, deliberately — the same
              idiom this client already uses on Setup Quality. A guard in
              notYetCards.test.ts fails if this copy contains the name of any
              section heading the tab renders, and it rightly cannot tell
              "explaining how X saves" from "claiming X is missing". It fired on
              the first draft of this sentence. */}
          The one exception is your daily pledge — a target or daily stop you type
          becomes a commitment only when you <b>lock</b> it, so it is kept then
          rather than as you type.
        </p>
        <p className="muted">
          {/* 🔴 "hide" CAME OFF THIS LIST IN THE COMMIT THAT BUILT IT (TCR-81).
              It used to read "and the ⊘ hide buttons that let you drop a section
              you never use" — and leaving that in would be the fourth stale
              inventory on this tab, which the header above has already counted
              three of. ⚠ The replacement is NOT a sentence naming the new
              control: `notYetCards.test.ts` fails if this copy mentions anything
              the tab renders, and the gear is on screen. Saying nothing is
              correct here — the gear explains itself. */}
          What is not here yet has no control on this tab at all: the{' '}
          <b>checklist gear</b> (adding and renaming items), <b>PNG export</b>, the{' '}
          <b>Discord post</b> and <b>Coach Me</b>. Acknowledging a breached daily stop belongs
          to the current client too — this one tells you the stop is hit and stops
          there. Everything named here is missing, not broken, and a save from this tab
          leaves every one of them untouched.
        </p>
      </div>
    </div>
  );
}

// ── Pre-Market ───────────────────────────────────────────────────────────────

function PrePanel(
  { plan, entry, priorFocus, date, rawPlan, rawContract, readinessGroups,
    baselineUpdatedAt, onSaved, onContractSaved, onReadinessSaved }:
  {
    plan: PreMarketPlan; entry: JournalEntry;
    priorFocus: { date: string; text: string } | null;
    date: string;
    /** The row's stored blob, passed straight through to the writer. */
    rawPlan: Record<string, unknown>;
    /** ⚠ The CONTRACT blob, separately. See `rawContractFor` on useJournal. */
    rawContract: Record<string, unknown>;
    /** ⚠ From `user_preferences`, resolved once by the tab. Never a constant. */
    readinessGroups: readonly ReadinessGroup[];
    baselineUpdatedAt: string | null;
    onSaved: (
      plan: PreMarketPlan, disqualifiers: string,
      updatedAt: string | null, rawPlan: Record<string, unknown>,
    ) => void;
    onContractSaved: (
      contract: SessionContract, updatedAt: string | null,
      rawContract: Record<string, unknown>,
    ) => void;
    onReadinessSaved: (
      items: Record<string, boolean>, completedAt: string | null,
      updatedAt: string | null,
    ) => void;
  },
) {
  // ⚠ THE FORM IS THE SURFACE NOW. The read-only Market Read / Key levels /
  // Setups / Self-assessment / News sections were removed when the editor landed:
  // the form renders the SAVED values in its own inputs, so a second copy above it
  // showed every field twice and disagreed the moment anything was typed. The old
  // client's Pre-Market tab is a form too.
  //
  // What stays read-only here is what NEITHER form owns: the carried-forward
  // focus and the attachments. The session contract is now its own editor, with
  // its own writer and its own five-of-nine field ownership.
  return (
    <div className="jp-panel">
      {/* ⚠ "For you today" is rendered by JournalTab, not here — see the note at
          that call site. It must appear on a day with NO entry too, and this
          panel does not exist on those days. Its position (above the carried
          focus) matches the old client's order in both layouts. */}

      {/* ⚠ Carried forward from the most recent journalled day, read-only. It is
          the one thread that connects yesterday's review to today's plan, and
          losing it makes "Tomorrow's Focus" write-only. */}
      {priorFocus ? (
        <div className="jp-carry">
          {/* ⚠ "Wed, 7/8", not "2026-07-08". The heading's whole job is to say how
              stale the thought is, and a bare ISO date does not answer that. */}
          <div className="jp-carry-h">Today’s Focus · carried from {dayLabel(priorFocus.date)}</div>
          <div className="jp-prose-text">{priorFocus.text}</div>
        </div>
      ) : null}

      {/* ⚠ NO WRAPPER SECTION. This used to sit inside a card titled "Edit the
          plan", which put five section cards inside a sixth — a border and a
          collapse chevron around the entire plan. Beta's pre-market panel drops
          straight into the sections, and the editor renders them itself. */}
      {/* ⚠ 🚨 `key={date}` — THIS IS A DATA GUARD, NOT A RENDER HINT.
            `PlanEditor` seeds its draft with `useState(plan)`, which runs ONCE.
            Without the key, stepping ‹ or › keeps the previous day's draft in the
            form while the date, the raw blob and the save target all move to the
            new day — and `dirty` then reads true, so Save is live. Pressing it
            writes yesterday's plan (or, from an unjournalled day, a set of
            BLANKS) over the new date's row.

            Observed on the live page: 7/31 (no entry) → ‹ → 7/30 showed empty
            regime/gamma/bias selects while the glance band two inches above read
            "BULL/NEUTRAL · PDC/TRENDING BULLISH regime" off the same entry. One
            Save would have erased them. This is D61 exactly — a blank the user
            confirms is a fully authorised delete — and no test saw it.

            The key remounts, which discards EVERY field at once. A reset effect
            would have to enumerate the state and would silently miss the next
            field added. */}
        <PlanEditor
          key={date}
          plan={plan} disqualifiers={entry.disqualifiers} date={date}
          rawPlan={rawPlan} baselineUpdatedAt={baselineUpdatedAt} onSaved={onSaved}
        />

      {/* ⚠ EDITABLE, and it replaced a read-only "Contract check-in" card. Josh's
          screenshot of beta shows the contract as a live control on this tab —
          target, DLL and Lock/Unlock. The port had withheld it on the grounds that
          it "carries a state machine", which was half right and pointed the wrong
          way: the state machine constrains WHICH FIELDS the form writes, not
          whether the form exists.

          ⚠ Same `key={date}` guard as the plan editor, and for the same reason
          (D65) — it seeds its inputs with `useState`.

          ⚠ ORDERING DELTA, deliberate and noted for the visual-parity pass: beta
          places this BETWEEN Setups Hunting and Self-Assessment, i.e. inside the
          run of plan sections. Here it sits after the plan form, because the plan
          form is one save unit and the contract is another — interleaving them
          would put two different writers' controls inside one visual block. */}
      {/* ⚠ NO `<Sec>` WRAPPER. The editor renders its own heading with the
          locked/unlocked pill beside it, and wrapping it printed "SESSION
          CONTRACT" twice, one above the other. Caught on the live page. */}
      <SessionContractEditor
        key={'contract-' + date}
        contract={entry.sessionContract} date={date}
        rawContract={rawContract} baselineUpdatedAt={baselineUpdatedAt}
        onSaved={onContractSaved}
      />

      {/* ⚠ Date-keyed (D65) even though this component holds no draft today: its
          `busyKey` and `error` are per-day state, and the next person to add a
          draft here inherits the guard rather than having to remember it.

          ⚠ PREFIXED, and the bare `key={date}` on both siblings was a REAL BUG:
          React warns "encountered two children with the same key… may cause
          children to be duplicated and/or omitted", and it duplicated — the
          contract editor rendered TWICE, which six tests caught as "found
          multiple elements". A date alone is not unique among siblings. */}
      <ReadinessGate
        key={'readiness-' + date}
        items={entry.readiness.items} completedAt={entry.readiness.completedAt}
        groups={readinessGroups} date={date}
        baselineUpdatedAt={baselineUpdatedAt} onSaved={onReadinessSaved}
      />

      {/* ⚠ THE "ATTACHMENTS" APOLOGY THAT STOOD HERE IS DELETED, NOT AMENDED.
          It counted the screenshots and said displaying them "needs signed
          Storage URLs" — and beta never used a signed URL for any of this. It
          downloads the object through the authenticated client and calls
          `URL.createObjectURL` on the blob. So the sentence was wrong about the
          MECHANISM as well as out of date, and the whole surface it described
          now renders: chart screenshots inside the plan form (beta's position,
          between Market Read and News), voice notes on the Recap panel. D84 —
          the copy is checked against the page. */}
    </div>
  );
}





// ── Session ──────────────────────────────────────────────────────────────────

function SessionPanel(
  { entry, date, tilt, pending, livePnlUsd, rawContract, baselineUpdatedAt,
    onCheckIn, onNotesSaved }:
  {
    entry: JournalEntry; date: string; tilt: TiltResult | null; pending: boolean;
    /** The imported figure for the day, or null while the trades load. */
    /** ⚠ DOLLARS, never the display unit — the contract's rules are dollars. */
    livePnlUsd: number | null;
    rawContract: Record<string, unknown>;
    baselineUpdatedAt: string | null;
    onCheckIn: (
      contract: SessionContract, updatedAt: string | null,
      rawContract: Record<string, unknown>,
    ) => void;
    onNotesSaved: (notes: MidSessionNote[], updatedAt: string | null) => void;
  },
) {
  const p = entry.preMarketPlan;
  /**
   * The plan reference, ported from `sessionPlanRef` — during the session the
   * plan is a reminder, not a form.
   *
   * ⚠ A `.jp-planref` STRIP, not a section card. It is one line above the
   * sections, styled with the accent left-border, and the port had it as a
   * fourth collapsible card titled "Your plan today" — which put the day's
   * standing instruction behind the same chevron as everything else.
   *
   * ⚠ THE TOP HYPOTHESIS IS PART OF IT, and it was missing. Beta names the first
   * hypothesis with a setup written — that sentence is the actual plan, and bias
   * and regime without it are a mood.
   */
  const bits: ReactNode[] = [];
  if (p.overallBias.trim()) bits.push(<>bias <b>{p.overallBias}</b></>);
  if (p.marketRegime.trim()) bits.push(<>regime {p.marketRegime}</>);
  const topHypo = p.hypotheses.find((h) => h.setup.trim());
  if (topHypo) bits.push(<>top hypothesis: <i>“{topHypo.setup.trim()}”</i></>);
  if (entry.disqualifiers.trim()) {
    bits.push(<><b>disqualifier:</b> {entry.disqualifiers.trim()}</>);
  }

  return (
    <div className="jp-panel">
      <div className="jp-planref">
        {bits.length ? (
          <><b>Your plan today:</b>{' '}
            {bits.map((b, i) => (
              <span key={i}>{i ? ' · ' : ''}{b}</span>
            ))}
          </>
        ) : (
          <span className="muted">
            No pre-market plan logged yet — switch to Pre-Market to set one.
          </span>
        )}
      </div>

      {/* ⚠ BETA'S ORDER: check-in, notes, tilt. It is the order of the session
          itself — where do I stand, what did I see, am I losing the plot — and
          the port had it reversed with the plan behind a card. */}
      <Sec icon="⛨" title="Contract Check-In"
        {...(hasCheckInContract(entry.sessionContract) ? {} : { meta: 'no contract set' })}>
        <ContractCheckIn
          key={`checkin-${date}`}
          contract={entry.sessionContract} date={date} livePnlUsd={livePnlUsd}
          rawContract={rawContract} baselineUpdatedAt={baselineUpdatedAt}
          onSaved={onCheckIn}
        />
      </Sec>

      <Sec icon="✎" title="Mid-Session Notes" meta="live log">
        {/* ⚠ `key={date}` — same data guard as every other editor on this tab.
            The component holds a draft note in local state, and a date step must
            not carry a half-typed observation onto another day. */}
        <MidSessionNotes
          key={`notes-${date}`}
          notes={entry.midSessionNotes} date={date}
          baselineUpdatedAt={baselineUpdatedAt} onSaved={onNotesSaved}
        />
      </Sec>

      {/* ⚠ TILT CHECK — four behavioural flags over the day's trades. Read-only,
          and it was missing from the first port even though it needs nothing but
          data already loaded. */}
      {/* ⚠ 🚨 A LABEL-AND-VALUE LIST, NOT BULLETS, and the copy is beta's. This
          rendered a `<ul>` with sentences like "Re-entered within 3 min of a
          loss: 2×" and a heading reading "2 of 4 signals firing". Beta puts the
          measure on the left, the figure right-aligned on the right, and colours
          THE FIGURE — so the four numbers line up in a column a trader can scan
          mid-session, which is the only time this card is looked at.

          ⚠ AND THE COLOUR MOVES FROM THE LINE TO THE VALUE. A whole row in red
          reads as "this row is broken"; a red number reads as "this number is
          the problem", which is what it means. */}
      <Sec icon="⚠" title="Tilt Check" meta="today’s trades">
        {pending ? <p className="muted">Loading the day’s trades…</p>
          : !tilt ? (
            // ⚠ "Not enough trades to assess" — NOT "no tilt detected". The second
            // is reassurance the data cannot support. Beta's exact sentence.
            <p className="muted">Not enough trades to assess tilt.</p>
          ) : (
            <>
              <p className={`tilt-verdict ${tilt.flags ? 'red' : 'green'}`}>
                {tilt.flags === 0
                  ? '✓ No tilt signals — clean session.'
                  : `⚠ ${tilt.flags} tilt signal${tilt.flags === 1 ? '' : 's'} flagged.`}
              </p>
              <TiltRow label="Longest losing streak" bad={tilt.maxStreak >= 3}
                value={`${tilt.maxStreak} trade${tilt.maxStreak === 1 ? '' : 's'}`} />
              <TiltRow label="P&L after your worst loss"
                bad={!!tilt.afterN && tilt.afterPnl < 0}
                value={tilt.afterN ? money(tilt.afterPnl, 0) : '—'} />
              <TiltRow label="Rapid re-entries (≤3m after a loss)"
                bad={tilt.revenge > 0} value={String(tilt.revenge)} />
              <TiltRow label="Size-ups right after a loss"
                bad={tilt.sizeUp > 0} value={String(tilt.sizeUp)} />
            </>
          )}
      </Sec>
    </div>
  );
}

// ── Recap ────────────────────────────────────────────────────────────────────

function RecapPanel(
  { entry, date, baselineUpdatedAt, onSaved, onRecordingsSaved }:
  {
    entry: JournalEntry; date: string;
    baselineUpdatedAt: string | null;
    onSaved: (entry: JournalEntry, updatedAt: string | null) => void;
    onRecordingsSaved: (recordings: string[], updatedAt: string | null) => void;
  },
) {
  const r = entry.eodRecap;
  const { sb, userId } = useSupabase();
  return (
    <div className="jp-panel">
      {/* ⚠ 🚨 THE "THE DAY" STATS STRIP THAT STOOD HERE IS DELETED. It printed Net
          P&L, Best and Worst — the same three numbers the glance band prints two
          inches above it, off the same aggregate. Beta has no such block on this
          phase, and two renderings of one fact on one screen is the shape that
          has produced a defect every time it has appeared here: they cannot
          disagree today, and the day one of them changes population they will,
          silently. The band is the one that stays. */}

      {/* ⚠ EDITABLE, and these two fields read from the CORRECTED entry. The
          database columns `eod_mistakes` and `eod_wins` hold each other's content
          for any entry the old client has not re-saved since its fix, and
          io/sync/journal.ts undoes that at the read boundary (D60). Nothing here
          may read or write a raw column. */}
      <Sec icon="◱" title="EOD Recap" hideId="recap:sec:eod">
        {/* ⚠ 🚨 `key={date}` — same data guard as the plan editor, same reason.
            `RecapEditor` seeds `draft` and `followed` with lazy `useState`, which
            runs once, so a date step would carry one day's review prose into
            another day's form with Save enabled. */}
        <RecapEditor
          key={date}
          entry={entry} date={date}
          baselineUpdatedAt={baselineUpdatedAt} onSaved={onSaved}
        />
      </Sec>

      {/* ⚠ ON THE RECAP PANEL, not Pre-Market. Beta puts Voice Notes here, right
          after the EOD Recap card (index.html:33066) — a voice note is what you
          leave at the end of a session, and the port's first pass had it filed
          under the pre-market attachments apology, which was wrong twice over.

          ⚠ `key={date}` for the same reason as the two editors above: this
          component holds per-day state (the recorder handle, the elapsed
          counter, its error) and a date step must not carry a live recorder
          from one day onto another. */}
      <Sec icon="◉" title="Voice Notes" hideId="recap:sec:voice">
        <VoiceNotes
          key={date}
          files={entry.recordings} prefix={`${date}-journal`}
          confirmRemove ownerLabel={date}
          onChange={async (next) => {
            const r = await saveRecordings({
              sb, userId, date, recordings: next, baselineUpdatedAt,
            });
            // ⚠ FOLDED ONLY ON A REAL WRITE. A held-stale save that advanced the
            // baseline would let the next attempt sail through the guard and
            // clobber exactly what the guard just protected.
            if (r.error) return r.error;
            onRecordingsSaved(next, r.updatedAt);
            return null;
          }}
        />
      </Sec>

      {/* ⚠ READ-ONLY, and deliberately so: the old client removed `eod_mood` and
          `eod_energy` from BOTH its writer and its reader in a Phase 2 audit, so
          they are orphan columns with no editor anywhere. Rows from May 2026 still
          hold real values ('Sick', 'Calm'), which is why they are still displayed
          and why the recap write does not send them — sending '' from a form that
          does not collect them would erase the only copy. */}
      {r.mood || r.energy ? (
        <Sec icon="❤" title="State" hideId="recap:sec:state">
          <dl className="kv">
            <dt className="muted">Mood</dt><dd>{r.mood || '—'}</dd>
            <dt className="muted">Energy</dt><dd>{r.energy || '—'}</dd>
          </dl>
          <p className="muted">
            Recorded by an older version of the app, which no longer offers these —
            kept visible, never overwritten.
          </p>
        </Sec>
      ) : null}
    </div>
  );
}

// ── bits ─────────────────────────────────────────────────────────────────────

/**
 * The four-cell band under the phase strip.
 *
 * ⚠ PER PHASE, and the cells differ — ported from `jpGlanceData`. It is pure
 * read and needs nothing the tab has not already loaded, which is exactly why
 * leaving it out was a gap rather than a deferral.
 */
function GlanceBand(
  { phase, entry, stats, readinessDone }:
  {
    phase: JournalPhase; entry: JournalEntry;
    /**
     * ⚠ ALWAYS IN DOLLARS. See the memo that builds it — the contract's target
     * and daily loss limit are the firm's dollar rules, and every tile in this
     * band is read against them.
     */
    stats: Kpis<TradeTableRow> | null;
    /** ⚠ Computed against the ACTIVE checklist — see the pill. Not `completedAt`. */
    readinessDone: boolean;
  },
) {
  const p = entry.preMarketPlan;
  const c = entry.sessionContract;
  const notes = entry.midSessionNotes.length;
  // ⚠ `moneyTarget`, not `money`. This is a goal, and "+$1,000.00" reads as a
  // gain already banked — the old client prints "$1,000" here.
  const target = c.targetProfit ? moneyTarget(c.targetProfit) : '—';
  // ⚠ Whether the trader pledged specific accounts. The Recap band SAYS which
  // population it counted, because "Plan followed" over the whole book and over
  // three pledged accounts are different claims and the tile cannot show both.
  const scoped = c.accountIds.length > 0;

  // ⚠ `ic` is the tile's icon glyph, and the icons are the OLD CLIENT'S — the
  // same character per concept, so the two screens read alike. This client had
  // no icon at all because it rendered these tiles with the Trades tab's
  // `.stat-cell` instead of the journal's `.jp-gtile`.
  let cells: { ic: string; k: string; v: string; sub?: string; cls?: string }[];

  if (phase === 'pre') {
    cells = [
      // ⚠ The sub-line is not decoration: "Incomplete" alone does not say WHAT is
      // incomplete, and the readiness gate is not on this screen to explain it.
      { ic: '◷', k: 'Plan status', v: readinessDone ? 'Ready' : 'Incomplete',
        sub: readinessDone ? 'readiness complete' : 'readiness pending' },
      { ic: '⛨', k: 'Contract', v: c.locked ? 'Locked' : 'Open', sub: `target ${target}` },
      // ⚠ ◈ and ⚑ are beta's glyphs for these two. The port had ◆ and ▰, which is
      // the shape of defect this whole pass is about — near enough to look
      // deliberate, different enough that the two screens do not read alike.
      { ic: '◈', k: 'Bias', v: p.overallBias || '—', sub: p.marketRegime ? `${p.marketRegime} regime` : '' },
      { ic: '⚑', k: 'Disqualifiers', v: entry.disqualifiers.trim() ? '1 set' : 'none' },
    ];
  } else if (phase === 'session') {
    const hasContract = hasCheckInContract(c);
    const override = c.manualPnl != null && Number.isFinite(c.manualPnl);
    const live = stats?.totalPnl ?? null;
    const pnl = override ? (c.manualPnl as number) : live;
    const dll = c.personalDLL;
    // ⚠ DLL usage only counts a LOSS against the cap; a green day uses 0% of it.
    const usedPct = dll && pnl != null && pnl < 0
      ? Math.round(Math.min(1, -pnl / dll) * 100) : 0;
    // ⚠ AN EM DASH WHEN THERE IS NEITHER A CONTRACT NOR A TRADE. "$0.00" on a
    // day with nothing on it states a flat session; the trader has simply not
    // started. Beta's `haveP` guard, which the port had dropped.
    const haveP = hasContract || (stats != null && stats.count > 0);
    cells = [
      { ic: '∑',
        // ⚠ THE LABEL FOLLOWS THE SOURCE. A self-reported figure announced as
        // "Live P&L" is the one number on this screen that must never be
        // mistaken for the imported book.
        k: override ? 'Logged P&L' : 'Live P&L',
        v: haveP && pnl != null ? money(pnl) : '—',
        sub: hasContract ? (override ? 'self-reported' : 'imported trades') : 'no contract',
        cls: haveP && pnl != null ? (pnl >= 0 ? 'pos' : 'neg') : '' },
      // ── 🚨 THIS CARD STAYS IN DOLLARS, WHATEVER THE UNIT SWITCH SAYS ──
      //
      // The profit target and the daily loss limit are the FIRM’S rules, stated
      // and enforced in dollars. And the P&L above is read AGAINST the limit —
      // "room" is the limit minus what you are down — so converting one and not
      // the other would compare points to dollars, and converting both would
      // measure your day against a rule nobody has.
      { ic: '⛨', k: 'Target', v: target, sub: 'profit goal' },
      { ic: '▰', k: 'DLL Used',
        v: dll ? `${usedPct}%` : (c.personalDLL ? moneyTarget(c.personalDLL) : '—'),
        sub: dll ? `room ${money(Math.max(0, dll + Math.min(0, pnl ?? 0)))}` : 'personal cap' },
      { ic: '✎', k: 'Notes', v: String(notes), sub: notes ? 'logged today' : 'none yet' },
    ];
  } else {
    const followed = entry.booleans['preMarketPlanFollowed'];
    const planCell = {
      ic: '◷',
      k: 'Plan Followed',
      v: followed === true ? 'Yes' : followed === false ? 'No' : '—',
      // ⚠ WHICH ACCOUNTS THE ROW BESIDE IT COUNTED. Beta prints this and the port
      // did not, which left a Net P&L with no statement of what it was over.
      sub: scoped ? 'contract accounts' : 'all accounts',
      cls: followed === true ? 'pos' : followed === false ? 'neg' : '',
    };
    if (!stats || stats.count === 0) {
      cells = [
        planCell,
        { ic: '◆', k: 'Lessons', v: r0(entry.eodRecap.lessons), sub: 'no trades logged' },
        // ⚠ "Set", not "Logged" — beta's word for this tile, and the two tiles
        // sit side by side, so using one word for both hides which is which.
        { ic: '→', k: 'Carry-forward',
          v: entry.eodRecap.tomorrowFocus.trim() ? 'Set' : '—',
          sub: entry.eodRecap.tomorrowFocus.trim() ? 'to tomorrow' : '' },
        { ic: '✎', k: 'Notes', v: String(notes) },
      ];
    } else {
      // ⚠ `bestValue`, not the trade’s own `netPnl`: the ranking runs on the
      // value the stats were built from, so reading `netPnl` here would name the
      // right trade and state a different number.
      //
      // ⚠ THOSE STATS ARE NOW ALWAYS IN DOLLARS (#67), so today the two agree.
      // The indirection stays because it is the correct one — it follows
      // whatever the band is built from rather than assuming.
      const best = stats.bestValue;
      const worst = stats.worstValue;
      cells = [
        { ic: '∑', k: 'Net P&L', v: unitValue(stats.totalPnl, stats.unit),
          sub: `${stats.count} trade${stats.count === 1 ? '' : 's'} · ${pct(stats.winRate, 0)} win`,
          cls: stats.totalPnl >= 0 ? 'pos' : 'neg' },
        // ⚠ 🚨 THE COLOUR FOLLOWS THE NUMBER, NOT THE LABEL. These were hardcoded
        // `pos` and `neg`, so on an all-red day the best trade — still a loss —
        // was painted as a gain, and on an all-green day the worst was painted as
        // a loss. The tile that is wrong is the one a trader looks at on their
        // worst day.
        { ic: '▲', k: 'Best', v: unitValue(best, stats.unit),
          sub: stats.bestTrade?.contract ?? '',
          cls: (best ?? 0) >= 0 ? 'pos' : 'neg' },
        { ic: '▼', k: 'Worst', v: unitValue(worst, stats.unit),
          sub: stats.worstTrade?.contract ?? '',
          cls: (worst ?? 0) >= 0 ? 'pos' : 'neg' },
        planCell,
      ];
    }
  }

  return (
    /* ⚠ `.jp-glance` / `.jp-gtile`, NOT the Trades tab's `.trade-stats-strip` /
       `.stat-cell`. Borrowing that component is why these tiles had no icon
       badge and sat in a plain strip — the journal's own rules were already
       written and simply never relocated (`npm run relocate -- jp-`).

       ⚠ The 1px gap over a border-coloured background is how the hairlines
       between tiles are drawn: the container shows through. Do not "fix" it to
       a border. */
    <div className="jp-glance" data-testid="jp-glance">
      {cells.map((cl) => (
        <div className="jp-gtile" key={cl.k}>
          <div className="gic" aria-hidden="true">{cl.ic}</div>
          <div className="glabel">{cl.k}</div>
          <div className={`gval ${cl.cls ?? ''}`}>{cl.v}</div>
          {/* ⚠ A non-breaking space when there is no sub-line, matching beta:
              without it the tiles in a row end at different heights. */}
          <div className="gsub">{cl.sub ? cl.sub : ' '}</div>
        </div>
      ))}
    </div>
  );
}

/** "Logged" vs an em dash — the glance band states presence, not content. */
const r0 = (s2: string): string => (s2.trim() ? 'Logged' : '—');

/**
 * One Tilt Check measure: name on the left, figure right-aligned.
 *
 * ⚠ THE FIGURE CARRIES THE COLOUR, not the row. Relocated from beta's `row()`
 * inside `dvTiltHtml` — muted label, `green`/`red` value at weight 600.
 */
function TiltRow({ label, value, bad }: { label: string; value: string; bad: boolean }) {
  return (
    <div className="tilt-row">
      <span className="muted">{label}</span>
      <span className={bad ? 'red' : 'green'}>{value}</span>
    </div>
  );
}

/**
 * ⚠ 🚨 THIS FILE USED TO DEFINE ITS OWN `Sec`, AND THAT WAS THE DEFECT.
 *
 * It rendered `.jp-sec` as a bare `<h4 class="jp-sec-title">` with no header
 * row — no icon badge, no chevron, no collapse. `PlanEditor` had the real card.
 * So when Josh's side-by-side fixed the Pre-Market phase, the Session and Recap
 * phases, which nobody had compared against beta, silently kept the old one:
 * the same class name meaning two different things on the same screen.
 *
 * The relocation report cannot see this. It compares class names against the
 * OLD client and found `jp-sec` present in both. It has nothing to say about two
 * components in THIS client disagreeing about one class.
 *
 * ⚠ `Stat` went with it — the only caller was the Recap panel's duplicate stats
 * strip, deleted above.
 */
const Sec = JournalSection;
